/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useContext, useState } from "react";
import { isValidEmail } from "@tomocredit/common-fe/utils/validation";
import { BreakpointContext } from "components/common";
import gradientBorder from "images/pricing-gradient-border.svg";
import closeIcon from "images/x.svg";
import chevronDoubleArrowIcon from "images/chevron-double-bottom.svg";
import {
  TierTileMobile,
  TierPricingInfo,
  calculatePerMonthPrice,
  JoinButton,
  TierFeature,
} from "./BoostPricingHelpers";
import "../BoostLandingStyle.css";
import {
  BOOST_SUBSCRIPTION_FEATURES,
  BOOST_SUBSCRIPTION_FEATURE_PERKS,
  boostProductTierConfig,
} from "../consts";
import EmailInputDrawer from "./EmailInputDrawer";

const TABLE_COLUMN_WIDTH = 312;
const TABLE_ROW_HEIGHT = 56;
const TABLE_HEADER_HEIGHT = 248;

/**
 * @callback redirectToCheckout
 * @param {string} tier
 * @param {string} term
 * @param {string} email
 * @returns {void}
 */

/**
 * @callback setCheckoutTier
 * @param {string} tier
 * @returns {void}
 */

/**
 * @param {Object} props
 * @param {Object} props.tiers - The tiers of the boost product
 * @param {string} props.checkoutTier - The tier that is being checked out
 * @param {string} props.checkoutTerm - The term that is being checked out
 * @param {string} props.highlightTier - The term that is being checked out
 * @param {setCheckoutTier} props.setCheckoutTier - The function to set the checkout tier
 * @param {redirectToCheckout} props.redirectToCheckout - The function to redirect to the checkout page
 * @returns
 */
export const BoostPricingComponent = ({
  tiers = [],
  checkoutTier,
  checkoutTerm,
  checkoutEmail,
  setCheckoutTier,
  redirectToCheckout,
  highlightTier = "",
}) => {
  const { isBelow } = useContext(BreakpointContext);
  const [isEmailInputVisible, setIsEmailInputVisible] = useState(false);
  const tierConfig = boostProductTierConfig[checkoutTerm]?.[checkoutTier];
  const { name = "", price = 0, originalPrice } = tierConfig || {};
  const { perMonthPrice: selectedTierPerMonthPrice } = calculatePerMonthPrice(
    checkoutTerm,
    price,
    originalPrice,
  );

  /**
   * @param {string} email
   */
  const proceedToCheckout = (email) => {
    setIsEmailInputVisible(false);
    redirectToCheckout(checkoutTier, checkoutTerm, email);
  };

  const buttonClickCallback = (tier, term) => {
    setCheckoutTier(tier);
    if (checkoutEmail && isValidEmail(checkoutEmail)) {
      redirectToCheckout(tier, term, checkoutEmail);
    } else {
      setIsEmailInputVisible(true);
    }
  };

  const tableHeaderStyle = {
    width: TABLE_COLUMN_WIDTH,
    height: TABLE_HEADER_HEIGHT,
    borderWidth: 1,
    borderColor: "#4B4B4B",
  };

  const rowStyle = {
    height: TABLE_ROW_HEIGHT - 2,
  };

  // height = header + (row * number of features) - random offset to match the design
  const tableHeight =
    TABLE_HEADER_HEIGHT +
    TABLE_ROW_HEIGHT * Object.keys(BOOST_SUBSCRIPTION_FEATURES).length;

  return (
    <>
      <table
        className="hidden xl:table xl:table-fixed relative rounded-corners"
        style={{
          maxWidth: ((tiers?.length || 0) + 1) * TABLE_COLUMN_WIDTH,
          height: tableHeight,
          borderSpacing: 0,
        }}
      >
        {tiers.indexOf(highlightTier) !== -1 && (
          <img
            src={gradientBorder}
            alt=""
            style={{
              position: "absolute",
              top: -8,
              left: (tiers.indexOf(highlightTier) + 1) * TABLE_COLUMN_WIDTH - 4, // random offset to match design
              pointerEvents: "none",
            }}
          />
        )}
        <thead>
          <tr>
            <th style={{ ...tableHeaderStyle, borderWidth: 0, borderBottomWidth: 1 }} />
            {tiers.map((tier, idx) => {
              const _tierConfig = boostProductTierConfig[checkoutTerm]?.[tier];
              const isHighlighted = tier === highlightTier;
              const { perMonthPrice, perMonthOriginalPrice } =
                calculatePerMonthPrice(
                  checkoutTerm,
                  _tierConfig?.price,
                  _tierConfig?.originalPrice,
                );
              const borderTopLeftRadius = idx === 0 ? isHighlighted ? 0 : 10 : 0; // remove border radius for the first column if highlighted
              const borderTopRightRadius = idx === tiers.length - 1 ? isHighlighted ? 0 : 10 : 0; // remove border radius for the last column if highlighted
              const borderBottomRightRadius = idx === tiers.length - 1 ? isHighlighted ? 0 : 10 : 0; // remove border radius for the last column if highlighted
              return (
                <th
                  key={tier}
                  style={{
                    ...tableHeaderStyle,
                    borderWidth: isHighlighted
                      ? 0
                      : tableHeaderStyle.borderWidth,
                    borderBottomWidth: 1, // always show bottom border to separate header from body
                    borderTopLeftRadius,
                    borderTopRightRadius,
                    borderBottomRightRadius,
                  }}
                  className="p-0 m-0 cursor-pointer"
                  onClick={() => buttonClickCallback(tier, checkoutTerm)}
                >
                  <div className="relative flex flex-col w-full h-full pb-8">
                    <TierPricingInfo
                      tierName={_tierConfig?.name}
                      price={perMonthPrice}
                      originalPrice={perMonthOriginalPrice}
                      label={_tierConfig?.label}
                    />
                    <JoinButton
                      title={`Get ${_tierConfig?.name}`}
                      isHighlighted={isHighlighted}
                    />
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {Object.keys(BOOST_SUBSCRIPTION_FEATURES).map((feature) => {
            return (
              <tr key={feature} style={rowStyle}>
                <td className="font-bold text-base">
                  {BOOST_SUBSCRIPTION_FEATURE_PERKS[feature]}
                </td>
                {tiers.map((tier) => {
                  const _tierConfig =
                    boostProductTierConfig[checkoutTerm]?.[tier];
                  const featureInConfig = _tierConfig?.features?.find(
                    (f) => f.title === feature,
                  );
                  return (
                    <td key={tier} className="text-center">
                      {featureInConfig ? (
                        <div className="flex items-center justify-center">
                          <TierFeature
                            key={featureInConfig.title}
                            feature={featureInConfig}
                          />
                        </div>
                      ) : (
                        <span className="flex items-center justify-center w-full">
                          <img
                            src={closeIcon}
                            alt="x"
                            style={{ width: 24, height: 24, filter: "invert(60%) sepia(0%) saturate(0%) hue-rotate(151deg) brightness(94%) contrast(90%)" }}
                          />
                        </span>
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
      <div
        className="visible w-full flex flex-col flex-wrap items-center justify-center px-6 xl:hidden  mdl:flex-row  mdl:space-x-6 mdl:space-x-0 mdl:px-0"
      >
        {tiers.map((t, idx) => (
          <>
            <div
              className={`mb-4 w-full ${
                idx > 0 ? "mt-6" : "mt-0"
              } mdl:mb-0 mdl:mt-0`}
              style={{
                maxWidth: isBelow("mdl") ? 342 : 257, // mdl -> mobile breakpint
              }}
            >
              <TierTileMobile
                tier={t}
                selectedTier={checkoutTier}
                selectedTerm={checkoutTerm}
                handleClick={() => buttonClickCallback(t, checkoutTerm)}
                isHighlighted={t === highlightTier}
              />
            </div>
            {idx === 0 && (
              <span
                className="visible mdl:hidden flex items-center"
              >
                <img
                  src={chevronDoubleArrowIcon}
                  alt="More plans"
                  style={{
                    height: 16,
                    width: 16,
                    animation: "arrow-bounce 2s ease-in-out infinite",
                  }}
                />
                <p className="font-medium">More plans</p>
              </span>
            )}
          </>
        ))}
      </div>
      <EmailInputDrawer
        isVisible={isEmailInputVisible}
        onClose={() => setIsEmailInputVisible(false)}
        onSubmit={proceedToCheckout}
        name={name}
        price={selectedTierPerMonthPrice}
      />
    </>
  );
};
